import { getActivePinia } from 'pinia';
import { Parser } from '~/api';
import { SSHKey } from '~/api/models';
import { useUserStore } from '~/stores';
import RequestService from './request';

interface AccountUpdateFormData {
    action: string;

    // confirmation for email/password
    current_password?: string;

    // email
    email?: string;

    // password
    new_password?: string;
    new_password_confirmation?: string;
    invalidate_sessions?: boolean;

    // identity
    name_first?: string;
    name_last?: string;
    preferences?: Record<string, string>;

    // sso
    sso_redirect?: string;
    sso_code?: string;
    sso_state?: string;
}

class AccountService {
    update(data: AccountUpdateFormData) {
        return RequestService.put('/account', data).then((res) => {
            if (getActivePinia()) {
                const userStore = useUserStore();
                userStore.update(data);
            }

            return res;
        });
    }

    markNotificationsRead() {
        return RequestService.post('/account/notifications');
    }

    markAnnouncementsRead(data?: { ids?: number[] }) {
        return RequestService.post('/account/announcements', data);
    }

    dismissPhysgunFeedbackQuestionnaire() {
        return RequestService.delete('/account/physgun-feedback-questionnaire');
    }

    /**
     * Set or clear the user's per-node "primary server" preference.
     * Daemon places this user's SSH/code-server sidecar in the
     * named server's cgroup parent slice on that node.
     *
     *   - serverUuid set: node is derived from the server. Validates
     *     ssh.connect on the target.
     *   - serverUuid null: requires nodeId to identify which node's
     *     preference to clear.
     */
    setPrimaryServer(data: { serverUuid: string | null; nodeId?: number }) {
        return RequestService.patch('/account/preferences/primary-server', {
            server_uuid: data.serverUuid,
            node_id: data.nodeId,
        }).then((res) => {
            // Update the local user store so the SSH page's dropdown
            // reflects the change without a full reload. Per-node map
            // is keyed on stringified node id (matches the backend
            // JSON-key shape).
            if (getActivePinia() && data.nodeId !== undefined) {
                const userStore = useUserStore();
                const current = userStore.data;
                if (current) {
                    const map = { ...(current.primaryServerByNode || {}) };
                    if (data.serverUuid === null) {
                        delete map[String(data.nodeId)];
                    } else {
                        map[String(data.nodeId)] = data.serverUuid;
                    }
                    userStore.update({ primaryServerByNode: map } as any);
                }
            }
            return res;
        });
    }

    getSSHKeys(req: PaginatableRequest): Promise<ListResponse> {
        return RequestService.get('/security/ssh-keys', req).then(Parser.parse);
    }

    createSSHKey(data: { public_key: string; reauth_token: string }): Promise<SSHKey> {
        const { reauth_token, ...body } = data;

        return RequestService.post('/security/ssh-keys', body, {
            headers: { 'X-Reauth-Token': reauth_token },
        }).then(Parser.parse);
    }

    deleteSSHKey(data: { id: number }): Promise<void> {
        return RequestService.delete(`/security/ssh-keys/${data.id}`);
    }
}

export default new AccountService();
